/**
 * Helpers to show the actual time range for analytics period selectors.
 * Use API periodStart/periodEnd when available so the UI reflects the exact range queried.
 *
 * API-supported periods (analyticsController): 1d|today, 7d|week, 30d|month, 12m|year.
 * Each page may offer a subset:
 * - Dashboard / Analytics / Company: week, month, year, all (all → 12m).
 * - Analytics (Meeting Scorecard section): Last meeting (no period), Today (1d), Month (30d), Year (12m), Custom.
 */
import { format, startOfDay, subDays } from 'date-fns'

const PERIOD_DAYS = {
  '1d': 1,
  today: 1,
  '7d': 7,
  week: 7,
  '30d': 30,
  month: 30,
  '12m': 365,
  year: 365,
  all: 365,
}

/**
 * Format two ISO date strings as a short date range (e.g. "Mar 1 – Mar 7, 2026").
 * @param {string} [periodStartISO]
 * @param {string} [periodEndISO]
 * @returns {string} Formatted range or empty string if missing
 */
export function formatPeriodDateRange(periodStartISO, periodEndISO) {
  if (!periodStartISO || !periodEndISO) return ''
  const start = new Date(periodStartISO)
  const end = new Date(periodEndISO)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''
  const sameYear = start.getFullYear() === end.getFullYear()
  if (sameYear) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`
}

/**
 * Compute start/end dates for a period (for display when API bounds are not available).
 * @param {string} period - e.g. '7d', '30d', '12m'
 * @returns {{ start: Date, end: Date }}
 */
export function getPeriodBounds(period) {
  const p = String(period || '7d').toLowerCase()
  const days = PERIOD_DAYS[p] ?? PERIOD_DAYS['7d']
  const end = startOfDay(new Date())
  const start = subDays(end, days - 1)
  return { start, end }
}

/**
 * Get display label and date range text for the analytics period.
 * Prefer API values when provided so the UI matches the backend range.
 * @param {string} period - e.g. '7d', '30d', '12m'
 * @param {string} [apiLabel] - from API (e.g. "Last 7 days")
 * @param {string} [periodStartISO] - from API
 * @param {string} [periodEndISO] - from API
 * @returns {{ label: string, dateRangeText: string }}
 */
export function getPeriodLabelAndRange(period, apiLabel, periodStartISO, periodEndISO) {
  const labels = {
    '1d': 'Today',
    today: 'Today',
    '7d': 'Last 7 days',
    week: 'Last 7 days',
    '30d': 'Last 30 days',
    month: 'Last 30 days',
    '12m': 'Last 12 months',
    year: 'Last 12 months',
    all: 'All time',
  }
  const label = apiLabel || labels[String(period).toLowerCase()] || labels['7d']

  let dateRangeText = ''
  if (periodStartISO && periodEndISO) {
    dateRangeText = formatPeriodDateRange(periodStartISO, periodEndISO)
  }
  if (!dateRangeText && period) {
    const { start, end } = getPeriodBounds(period)
    dateRangeText = formatPeriodDateRange(start.toISOString(), end.toISOString())
  }

  return { label, dateRangeText }
}
